import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { createDiscountApprovalRequest, getAllDiscountApprovalRequests } from './goodwill.thunks';
import { initialState } from './constants';

const goodwillAdapter = createEntityAdapter();

const nextMetadataAfterRemoval = (metadata = {}, fallbackPage = initialState.page) => {
	const perPage = Number(metadata?.per_page) || initialState.metadata.per_page;
	const currentTotalCount = Number(metadata?.total_count) || 0;
	const nextTotalCount = currentTotalCount > 0 ? currentTotalCount - 1 : 0;
	const nextPageCount = Math.max(Math.ceil(nextTotalCount / perPage), 1);
	const currentPage = Number(metadata?.page) || fallbackPage || initialState.metadata.page;

	return {
		...metadata,
		total_count: nextTotalCount,
		page_count: nextPageCount,
		page: Math.min(currentPage, nextPageCount),
	};
};

const goodwillSlice = createSlice({
	name: 'dealDriverGoodwill',
	initialState: {
		...initialState,
		data: goodwillAdapter.getInitialState(),
	},
	reducers: {
		updateGoodwillPage: (state, { payload }) => {
			state.page = payload;
		},
		updateGoodwillSorting: (state, { payload }) => {
			state.sorting = payload;
			state.page = 1;
			state.metadata.page = 1;
		},
		updateGoodwillStatuses: (state, { payload }) => {
			state.statuses = payload;
		},
		clearGoodwillErrors: (state) => {
			state.errors = initialState.errors;
		},
		mergeGoodwillRow: (state, { payload }) => {
			const approvalRequestId = payload?.id;
			if (!approvalRequestId || !state.data.entities[approvalRequestId]) return;

			const changes = { ...payload };

			if (changes.status && changes.status !== 'pending') changes.can_act = false;

			goodwillAdapter.updateOne(state.data, {
				id: approvalRequestId,
				changes,
			});
		},
		removeGoodwillRow: (state, { payload }) => {
			const approvalRequestId = payload?.id || payload;
			if (!approvalRequestId || !state.data.entities[approvalRequestId]) return;

			const nextMetadata = nextMetadataAfterRemoval(state.metadata, state.page);

			state.metadata = nextMetadata;
			state.page = nextMetadata.page;
			goodwillAdapter.removeOne(state.data, approvalRequestId);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAllDiscountApprovalRequests.pending, (state, { meta }) => {
				state.loading = true;
				state.currentRequestId = meta.requestId;
				state.success = undefined;
				state.errors.approvalRequestErrorMessage = '';
			})
			.addCase(getAllDiscountApprovalRequests.fulfilled, (state, { payload, meta }) => {
				if (state.currentRequestId !== meta.requestId) return;

				const { discountApprovalRequests: approvalRequests, metadata } = payload;

				if (!Array.isArray(approvalRequests)) return;

				goodwillAdapter.setAll(state.data, approvalRequests);
				state.metadata = metadata;
				state.page = metadata?.page ?? state.page;
				state.loading = false;
				state.currentRequestId = null;
				state.success = true;
			})
			.addCase(getAllDiscountApprovalRequests.rejected, (state, { payload, meta }) => {
				if (state.currentRequestId !== meta.requestId) return;

				state.loading = false;
				state.currentRequestId = null;
				state.success = false;
				state.errors.dataErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(createDiscountApprovalRequest.pending, (state) => {
				state.success = undefined;
				state.errors.approvalRequestErrorMessage = '';
			})
			.addCase(createDiscountApprovalRequest.fulfilled, (state, action) => {
				const { payload, meta } = action;
				const { requestId } = meta?.arg || {};
				const responseStatus = payload?.responseData?.data?.approval_request?.status;
				const requestStatus =
					responseStatus ||
					(meta?.arg?.isDenied ? 'denied'
					: meta?.arg?.isApproved ? 'approved'
					: null);
				const existingRow = requestId ? state.data.entities[requestId] : null;

				state.loading = false;
				state.success = true;

				if (!requestId || !existingRow || !requestStatus) return;

				const isBuyerRequest = Boolean(existingRow?.approval_request_summary?.buy_fee_discount);
				const contextKey = isBuyerRequest ? 'offer_context' : 'deal_context';
				const currentRequest = existingRow?.[contextKey]?.current_request;
				const hasContextRequiresConfirmation = Boolean(existingRow?.[contextKey]?.requires_confirmation);

				goodwillAdapter.updateOne(state.data, {
					id: requestId,
					changes: {
						status: requestStatus,
						[contextKey]: {
							...existingRow[contextKey],
							current_request: {
								...currentRequest,
								status: requestStatus,
							},
						},
						...(hasContextRequiresConfirmation ?
							{
								[contextKey]: {
									...existingRow[contextKey],
									requires_confirmation: false,
								},
							}
						:	{}),
					},
				});
			})
			.addCase(createDiscountApprovalRequest.rejected, (state, action) => {
				const { payload, meta } = action;
				const requestId = meta?.arg?.requestId;
				const existingRow = requestId ? state.data.entities[requestId] : null;

				state.loading = false;
				state.success = false;
				state.errors.approvalRequestErrorMessage = payload || 'Something went wrong, please try again.';

				const errorPayload = payload?.error || payload;
				const hasRequiresConfirmationError = Boolean(errorPayload?.requires_confirmation);
				const isBuyerRequest = Boolean(existingRow?.approval_request_summary?.buy_fee_discount);

				if (!requestId || !existingRow || !hasRequiresConfirmationError || !isBuyerRequest) return;

				goodwillAdapter.updateOne(state.data, {
					id: requestId,
					changes: {
						offer_context: {
							...existingRow.offer_context,
							requires_confirmation: true,
						},
					},
				});
			});
	},
});

export const {
	updateGoodwillPage,
	updateGoodwillSorting,
	clearGoodwillErrors,
	mergeGoodwillRow,
	removeGoodwillRow,
	updateGoodwillStatuses,
} = goodwillSlice.actions;

export const {
	selectIds: selectAllGoodwillIds,
	selectById: selectGoodwillById,
	selectEntities: selectGoodwillEntities,
	selectAll: selectAllGoodwillRows,
} = goodwillAdapter.getSelectors((state) => state.dealDriverGoodwill.data);

export default goodwillSlice.reducer;
